
import React, { useState, useEffect } from 'react';
import { loadGoogleScripts, authenticate, fetchFromDrive } from '../services/googleDriveService';
import { deleteProfileData, saveWinesLocally } from '../services/storageService';

interface ProfileSelectorProps {
  onSelect: (name: string) => void;
}

const ProfileSelector: React.FC<ProfileSelectorProps> = ({ onSelect }) => {
  const [profiles, setProfiles] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    loadGoogleScripts().catch(console.error);
    
    // Leggi l'elenco dei profili dalla nostra lista dedicata
    const profilesRaw = localStorage.getItem('divino_profiles_list') || '[]';
    const savedProfiles = JSON.parse(profilesRaw);
    
    // Fallback per vecchi profili (compatibilità)
    const legacyKeys = Object.keys(localStorage)
      .filter(k => k.startsWith('divino_cellar_'))
      .map(k => k.replace('divino_cellar_', ''));
    
    setProfiles([...new Set([...savedProfiles, ...legacyKeys])]);
  }, []);

  const handleGoogleLogin = async () => {
    setIsSyncing(true);
    try {
      const token = await authenticate();
      if (token) {
        const cloudProfileId = "Cloud_Backup"; 
        const cloudData = await fetchFromDrive(cloudProfileId);
        
        if (cloudData) {
          await saveWinesLocally(cloudProfileId, cloudData);
        }
        onSelect(cloudProfileId);
      }
    } catch (err) {
      console.error("Login failed:", err);
      alert("Impossibile connettersi a Google.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim()) {
      onSelect(newName.trim());
    }
  };

  const deleteProfile = async (e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    if (confirm(`Eliminare "${name}"? I dati verranno rimossi permanentemente.`)) {
      await deleteProfileData(name);
      setProfiles(prev => prev.filter(p => p !== name));
    }
  };

  return (
    <div className="h-full w-full bg-[#0a0503]/80 backdrop-blur-md flex flex-col items-center justify-center p-8 animate-scale-in">
      <header className="text-center mb-16 pt-[env(safe-area-inset-top)]">
        <h1 className="text-6xl font-bold text-amber-500 mb-2 serif tracking-tight">Divino</h1>
        <p className="text-slate-500 uppercase tracking-[0.5em] text-[10px] font-black">Sommelier Digitale</p>
      </header>

      <div className="w-full max-w-sm space-y-10">
        <button 
          onClick={handleGoogleLogin}
          disabled={isSyncing}
          className="w-full bg-white text-black font-black py-5 px-6 rounded-3xl flex items-center justify-center gap-4 shadow-2xl active:scale-95 transition-all disabled:opacity-50"
        >
          {isSyncing ? (
            <div className="w-5 h-5 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
          ) : (
            <svg viewBox="0 0 48 48" width="22" height="22"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
          )}
          <span className="text-sm uppercase tracking-widest">{isSyncing ? 'Accesso...' : 'Sincronizza Cloud'}</span>
        </button>

        <div className="relative flex items-center gap-4">
          <div className="flex-1 h-px bg-white/5"></div>
          <span className="text-[8px] font-black text-slate-700 uppercase tracking-[0.3em]">Oppure Locale</span>
          <div className="flex-1 h-px bg-white/5"></div>
        </div>

        <div className="space-y-4 max-h-[40vh] overflow-y-auto no-scrollbar pb-10">
          {profiles.filter(p => p !== "Cloud_Backup").map(p => (
            <div 
              key={p} 
              onClick={() => onSelect(p)}
              className="group bg-white/[0.03] p-6 rounded-3xl border border-white/5 flex items-center justify-between cursor-pointer active:scale-95 transition-all hover:border-amber-500/30"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-600/10 flex items-center justify-center text-amber-500 font-serif text-xl">{p[0].toUpperCase()}</div>
                <span className="text-amber-100 font-bold tracking-tight">{p}</span>
              </div>
              <button onClick={(e) => deleteProfile(e, p)} className="p-2 text-red-500/20 hover:text-red-500 transition-all">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
              </button>
            </div>
          ))}

          {!isCreating ? (
            <button 
              onClick={() => setIsCreating(true)}
              className="w-full py-6 border-2 border-dashed border-white/5 rounded-3xl text-slate-700 text-[10px] font-black uppercase tracking-widest transition-all"
            >
              + Nuovo Spazio Locale
            </button>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4 animate-scale-in">
              <input 
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nome Cantina..."
                className="w-full bg-white/5 border border-amber-500/30 rounded-3xl p-5 text-amber-100 focus:outline-none"
              />
              <div className="flex gap-3">
                <button type="button" onClick={() => setIsCreating(false)} className="flex-1 py-4 bg-white/5 text-slate-500 rounded-2xl text-[9px] font-black uppercase tracking-widest">Annulla</button>
                <button type="submit" disabled={!newName.trim()} className="flex-[2] py-4 bg-amber-600 text-white rounded-2xl text-[9px] font-black uppercase tracking-widest">Crea</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSelector;
